import React, { cloneElement, type JSX } from "react";

import Link from "next/link";
import { twMerge } from "tailwind-merge";

import { SupRightArrowAlt } from "@repo/core-ui";

interface LayoutAlertBarBannerProps {
  icon?: JSX.Element;
  title: string;
  description: string;
  link?: string;
  isLoading?: boolean;
  actionItem?: JSX.Element;
  isAnimated?: boolean;
}

export default function Banner({ title, description, link, isLoading = false, icon, actionItem }: LayoutAlertBarBannerProps) {
  const extendedIcon = icon ? (
    cloneElement(icon, { className: "mr-4 w-8 h-8 text-primary-500 md:w-10 md:h-10" })
  ) : (
    <img src="/assets/img/cred/cred-dark.svg" alt="Cred" className="h-8 w-8 text-primary-500 md:mr-2 md:h-10 md:w-10" />
  );
  const isExternalLink = link?.startsWith("http");

  const mainContent = (
    <div>
      <div
        className={twMerge(
          "flex h-full select-none flex-col space-y-2 px-8 py-5 uppercase tracking-[1px] text-primary-800",
          "rounded-bl-2xl rounded-tr-2xl border-0.5 border-primary-500",
          "bg-gradient-to-r from-primary-25 from-10% to-primary-300 to-[145%]",
          "md:flex-row md:items-center md:gap-0 md:space-x-2 md:space-y-0",
          link && "group-hover:from-primary-50 group-hover:to-primary-500"
        )}
      >
        {extendedIcon}

        <div className="flex flex-1 flex-col justify-between space-y-1">
          {!isLoading ? (
            <h4 className="font-mono font-bold leading-6 md:text-xl md:leading-6">{title}</h4>
          ) : (
            <div className="mb-1 inline-flex h-4 w-4/5 rounded-full bg-primary-300 md:h-5 md:w-80" />
          )}
          {!isLoading ? (
            <p className="font-mono text-xs  leading-4">{description}</p>
          ) : (
            <>
              <div className="inline-flex h-3 w-full rounded-full bg-primary-300 md:h-4 lg:w-[682px]" />
              <div className="inline-flex h-3 w-1/2 rounded-full bg-primary-300 md:hidden" />
            </>
          )}
        </div>

        {actionItem ? (
          actionItem
        ) : (
          <span
            className={twMerge(
              "block size-7 rounded-full bg-primary-800 text-primary-75 transition-colors duration-200 group-hover:bg-primary-700 md:ml-8 md:size-9",
              isLoading && "invisible"
            )}
          >
            <SupRightArrowAlt className="size-7 md:size-9" />
          </span>
        )}
      </div>
    </div>
  );

  return link ? (
    <Link className="group h-full w-full" href={!isLoading ? link : ""} target={isExternalLink ? "_blank" : undefined}>
      {mainContent}
    </Link>
  ) : (
    <div className="group h-full w-full">{mainContent}</div>
  );
}
